import axios from 'axios'

const client = axios.create({
  baseURL: process.env.REACT_APP_REPORT_API_URL || 'http://localhost:3007',
})

export interface TypeLevelBelief {
  level: string
  p: number
  alpha: number
  beta: number
  hasLo: boolean
}

export interface TypeProgressData {
  type: string
  estimatedLevel: string
  score: number | null
  confidence: number
  directAnswerCount: number
  isInferred?: boolean
  probeLower: number
  probeUpper: number
  nextProbeLevel: string | null
  levels: TypeLevelBelief[]
  aiExplicit?: boolean
}

export interface CompetenceProgress {
  code: string
  competenceId: string
  competenceTitle?: string
  estimatedLevel: string
  score: number | null
  confidence: number
  directAnswerCount: number
  types: TypeProgressData[]
}

export interface AreaProgress {
  areaCode: string
  areaTitle?: string
  estimatedLevel: string | null
  score: number | null
  confidence: number
  competences: CompetenceProgress[]
  aiCorrect?: number
  aiTotal?: number
}

export interface ProgressResult {
  areas: AreaProgress[]
  overallScore: number | null
  kScore: number | null
  sScore: number | null
  aScore: number | null
}

export interface AttemptInfo {
  assessmentSlug: string | null
  assessmentName: string | null
  assessmentReportName: string | null
  assessmentDescription: string | null
  status: string
  createdAt: string
  completedAt: string | null
  totalQuestions: number
  resultCode: string | null
  candidate: { name: string; email: string; role: string | null } | null
}

export interface WrongOutcome {
  code: string
  description: string
  competenceCode: string
  areaCode: string
  areaTitle: string
  type: string
  proficiencyLevel: string
}

export interface RecommendedCourse {
  url: string
  title: string
  pageType: string | null
  seedUrl: string
  competenceCodes: string[]
  description: string
}

export interface CompetenceCourseRecommendationGroup {
  areaCode: string
  areaTitle: string
  competenceCode: string
  competenceTitle: string
  score: number
  estimatedLevel: string
  courses: RecommendedCourse[]
}

export interface AreaPrimaryRecommendationGroup {
  areaCode: string
  areaTitle: string
  competences: Array<{
    competenceCode: string
    competenceTitle: string
    score: number
    estimatedLevel: string
    courses: RecommendedCourse[]
  }>
}

export interface AreaCourseRecommendationGroup {
  areaCode: string
  areaTitle: string
  score: number
  estimatedLevel: string
  courses: RecommendedCourse[]
}

export interface CourseRecommendationsResult {
  attemptId: string
  resultCode: string | null
  assessmentSlug: string | null
  thresholds: {
    competenceScoreLt: number
    areaScoreLt: number
  }
  primaryByArea: AreaPrimaryRecommendationGroup[]
  primaryByCompetence: CompetenceCourseRecommendationGroup[]
  fallbackByArea: AreaCourseRecommendationGroup[]
}

export const PublicQuizAPI = {
  getProgress: async (attemptId: string): Promise<ProgressResult> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/progress`)
    return res.data
  },

  getAttemptInfo: async (attemptId: string): Promise<AttemptInfo> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/info`)
    return res.data
  },

  getWrongOutcomes: async (attemptId: string): Promise<{ outcomes: WrongOutcome[]; allOutcomes: WrongOutcome[] }> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/wrong-outcomes`)
    return { outcomes: res.data.outcomes ?? [], allOutcomes: res.data.allOutcomes ?? [] }
  },

  getCourseRecommendations: async (attemptId: string): Promise<CourseRecommendationsResult> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/courses`)
    return res.data
  },
}
